import { LitElement, html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { verifyConfidence } from '../services/memory-api.js';

type ConfidenceLevel = 'low' | 'medium' | 'high';

const LEVEL_LABELS: Record<ConfidenceLevel, string> = {
  low: 'Niedrig',
  medium: 'Mittel',
  high: 'Hoch',
};

@customElement('cp-memory-confidence-dialog')
export class CpMemoryConfidenceDialog extends LitElement {
  createRenderRoot() { return this; }

  @property({ type: Number, attribute: 'memory-id' }) memoryId = 0;
  @property({ type: String, attribute: 'current-confidence' }) currentConfidence = 'unknown';

  @state() private _open = false;
  @state() private _selected: ConfidenceLevel = 'medium';
  @state() private _working = false;
  @state() private _error = '';
  @state() private _done = false;

  private _dispatchVerified(level: ConfidenceLevel): void {
    this.dispatchEvent(new CustomEvent('memory-verified', {
      bubbles: true,
      composed: true,
      detail: { memoryId: this.memoryId, confidence: level },
    }));
  }

  private async _submit(): Promise<void> {
    if (this._working || !this.memoryId) return;
    this._working = true;
    this._error = '';
    try {
      await verifyConfidence(this.memoryId, this._selected);
      this._done = true;
      this._open = false;
      this._dispatchVerified(this._selected);
    } catch (e) {
      this._error = (e as Error).message;
    } finally {
      this._working = false;
    }
  }

  render() {
    if (this._done) {
      return html`<span class="cp-memory-confidence-dialog__done">✓ ${LEVEL_LABELS[this._selected]}</span>`;
    }
    return html`
      <div class="cp-memory-confidence-dialog">
        <button
          class="cp-memory-confidence-dialog__trigger"
          @click=${() => { this._open = !this._open; }}
        >${this.currentConfidence === 'unknown' ? 'Confidence setzen' : this.currentConfidence}</button>

        ${this._open ? html`
          <div class="cp-memory-confidence-dialog__panel">
            <div class="cp-memory-confidence-dialog__options">
              ${(['low', 'medium', 'high'] as ConfidenceLevel[]).map(lvl => html`
                <label class="cp-memory-confidence-dialog__option">
                  <input
                    type="radio"
                    name="confidence-${this.memoryId}"
                    value=${lvl}
                    ?checked=${this._selected === lvl}
                    @change=${() => { this._selected = lvl; }}
                  />
                  ${LEVEL_LABELS[lvl]}
                </label>
              `)}
            </div>
            ${this._error ? html`<div class="cp-memory-confidence-dialog__error">${this._error}</div>` : nothing}
            <div class="cp-memory-confidence-dialog__actions">
              <button
                class="cp-memory-confidence-dialog__btn cp-memory-confidence-dialog__btn--confirm"
                ?disabled=${this._working}
                @click=${this._submit}
              >${this._working ? '…' : 'Bestätigen'}</button>
              <button
                class="cp-memory-confidence-dialog__btn"
                @click=${() => { this._open = false; this._error = ''; }}
              >Abbrechen</button>
            </div>
          </div>
        ` : nothing}
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'cp-memory-confidence-dialog': CpMemoryConfidenceDialog;
  }
}
